/**
 * User profile service.
 * Handles profile CRUD and profile-related helpers.
 */

import { supabase } from '@/lib/integrations/supabase'
import { getDeviceInfo } from '@/lib/utils/deviceDetection'
import type { Theme } from '@/shared/contexts/ThemeContext'
import type { Profile, ProfileUpdate } from './profileTypes'

export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single()

  if (error && error.code !== 'PGRST116') throw error

  return data as Profile | null
}

export const getProfileByUserId = getProfile

export async function getPublicProfile(userId: string) {
  const profile = await getProfile(userId)

  if (!profile) return null

  const { full_name, avatar_url, theme } = profile as Profile & {
    avatar_url?: string | null
  }

  return { full_name, avatar_url, theme }
}

export async function updateProfile(
  userId: string,
  updates: ProfileUpdate,
): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .select()
    .single()

  if (error) throw error

  return data as Profile
}

export async function setProfileTheme(
  userId: string,
  theme: Theme,
): Promise<Profile> {
  return updateProfile(userId, { theme })
}

export async function upsertProfile(
  userId: string,
  data: ProfileUpdate,
): Promise<Profile> {
  const { data: updated, error: updateError } = await supabase
    .from('profiles')
    .update({
      ...data,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .select()
    .single()

  if (updated) {
    return updated as Profile
  }

  if (updateError?.code === 'PGRST116' || !updated) {
    const { data: profile, error: insertError } = await supabase
      .from('profiles')
      .insert({
        user_id: userId,
        ...data,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single()

    if (insertError) throw insertError

    return profile as Profile
  }

  if (updateError) throw updateError

  return updated as Profile
}

export async function updateLastAccess(userId: string): Promise<void> {
  const deviceInfo = getDeviceInfo()

  const { error } = await supabase
    .from('profiles')
    .update({
      ...deviceInfo,
      last_access: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId)

  if (error) throw error
}

export async function deleteProfile(userId: string): Promise<void> {
  const { error } = await supabase
    .from('profiles')
    .delete()
    .eq('user_id', userId)

  if (error) throw error
}

export async function createInitialProfile(
  userId: string,
  fullName?: string,
  phone?: string,
  facebook?: string,
  instagram?: string,
  tiktok?: string,
  email?: string,
): Promise<Profile> {
  const deviceInfo = getDeviceInfo()

  const { data, error } = await supabase
    .from('profiles')
    .insert({
      user_id: userId,
      full_name: fullName || null,
      email: email || null,
      phone: phone || null,
      whatsapp: phone || null,
      facebook: facebook || null,
      instagram: instagram || null,
      tiktok: tiktok || null,
      theme: 'light',
      ...deviceInfo,
    })
    .select()
    .single()

  if (error) throw error

  return data as Profile
}